/**
 * Permission Management API Types
 *
 * Types for managing role assignment permissions and permission bundles.
 */

import {
    AuthorizableAction,
    ResourceType,
    ALL_AUTHORIZABLE_ACTIONS,
    Role,
    isAllowedActionForResourceType,
} from '@pillar-portal/database/dist/frontend';
import { z } from 'zod';
import { CombinedConfigResponse } from './samba-config.types';
import { CombinedConfigResponseSchema } from './schemas/samba-config.schemas';
import type { OpaqueCursorPagination } from './authorization.types';
import type { OpaqueCursorSearchRequest } from './omnisearch.types';
import {
    RpaModuleName,
    RpaModuleNameSchema,
} from './backend_config.types';
import {
    AllInternalOnly,
    AllOrganizationalOnly,
} from '@pillar-portal/database/dist/frontend';
import {
    IpgSystemId,
    IpwSystemId,
} from './types/third-party-system.enums';

export interface OrganizationResourceDesignator {
    resource_type: typeof ResourceType.ORGANIZATION;
    organization_uuid: string;
    descendants: boolean;
}

export interface UserResourceDesignator {
    resource_type: typeof ResourceType.USER;
    user_uuid: string;
}

export interface RoleAssignmentResourceDesignator {
    resource_type: typeof ResourceType.ROLE_ASSIGNMENT;
    role_assignment_uuid: string;
}

export interface CredentialResourceDesignator {
    resource_type: typeof ResourceType.CREDENTIAL;
    credential_uuid: string;
}

export interface IpwResourceDesignator {
    resource_type: typeof ResourceType.IPW;
    ipw_system_id: IpwSystemId;
}

export interface IpgResourceDesignator {
    resource_type: typeof ResourceType.IPG;
    ipg_system_id: IpgSystemId;
}

export interface ProcessorResourceDesignator {
    resource_type: typeof ResourceType.PROCESSOR;
    processor_uuid: string;
}

export interface MerchantAccountResourceDesignator {
    resource_type: typeof ResourceType.MERCHANT_ACCOUNT;
    merchant_account_uuid: string;
}

export interface LogicalTerminalResourceDesignator {
    resource_type: typeof ResourceType.LOGICAL_TERMINAL;
    logical_terminal_uuid: string;
}

export interface TerminalDeviceResourceDesignator {
    resource_type: typeof ResourceType.TERMINAL_DEVICE;
    terminal_device_uuid: string;
}

export interface PermissionsBundleResourceDesignator {
    resource_type: typeof ResourceType.PERMISSIONS_BUNDLE;
    permissions_bundle_uuid: string;
}

export interface PermissionPolicyResourceDesignator {
    resource_type: typeof ResourceType.PERMISSION_POLICY;
    permission_policy_uuid: string;
}

export interface RpaModuleResourceDesignator {
    resource_type: typeof ResourceType.RPA_MODULE;
    rpa_module_name: RpaModuleName;
}

export const PermissionSourceDesignatorType = {
    WILDCARD: 'WILDCARD',
} as const;

export type PermissionSourceDesignatorType =
    (typeof PermissionSourceDesignatorType)[keyof typeof PermissionSourceDesignatorType];

export interface WildcardPermissionSourceDesignator {
    designator_type: typeof PermissionSourceDesignatorType.WILDCARD;
}

export type ResourceDesignator =
    | OrganizationResourceDesignator
    | UserResourceDesignator
    | RoleAssignmentResourceDesignator
    | CredentialResourceDesignator
    | IpwResourceDesignator
    | IpgResourceDesignator
    | ProcessorResourceDesignator
    | MerchantAccountResourceDesignator
    | LogicalTerminalResourceDesignator
    | TerminalDeviceResourceDesignator
    | PermissionsBundleResourceDesignator
    | PermissionPolicyResourceDesignator
    | RpaModuleResourceDesignator;

export type PermissionSourceDesignator =
    | ResourceDesignator
    | WildcardPermissionSourceDesignator;

const OrganizationResourceDesignatorSchemaBase = z.object({
    resource_type: z.literal(ResourceType.ORGANIZATION),
    organization_uuid: z.string().uuid(),
    descendants: z.boolean().default(false),
});
export const OrganizationResourceDesignatorSchema: typeof OrganizationResourceDesignatorSchemaBase =
    OrganizationResourceDesignatorSchemaBase;

const UserResourceDesignatorSchemaBase = z.object({
    resource_type: z.literal(ResourceType.USER),
    user_uuid: z.string().uuid(),
});
export const UserResourceDesignatorSchema: typeof UserResourceDesignatorSchemaBase =
    UserResourceDesignatorSchemaBase;

const RoleAssignmentResourceDesignatorSchemaBase = z.object({
    resource_type: z.literal(ResourceType.ROLE_ASSIGNMENT),
    role_assignment_uuid: z.string().uuid(),
});
export const RoleAssignmentResourceDesignatorSchema: typeof RoleAssignmentResourceDesignatorSchemaBase =
    RoleAssignmentResourceDesignatorSchemaBase;

const CredentialResourceDesignatorSchemaBase = z.object({
    resource_type: z.literal(ResourceType.CREDENTIAL),
    credential_uuid: z.string().uuid(),
});
export const CredentialResourceDesignatorSchema: typeof CredentialResourceDesignatorSchemaBase =
    CredentialResourceDesignatorSchemaBase;

const IpwResourceDesignatorSchemaBase = z.object({
    resource_type: z.literal(ResourceType.IPW),
    ipw_system_id: z.union([
        z.literal(IpwSystemId.DEALERTRACK),
        z.literal(IpwSystemId.PCPARTPICKER),
    ]),
});
export const IpwResourceDesignatorSchema: typeof IpwResourceDesignatorSchemaBase =
    IpwResourceDesignatorSchemaBase;

const IpgResourceDesignatorSchemaBase = z.object({
    resource_type: z.literal(ResourceType.IPG),
    ipg_system_id: z.literal(IpgSystemId.VALOR_US),
});
export const IpgResourceDesignatorSchema: typeof IpgResourceDesignatorSchemaBase =
    IpgResourceDesignatorSchemaBase;

const ProcessorResourceDesignatorSchemaBase = z.object({
    resource_type: z.literal(ResourceType.PROCESSOR),
    processor_uuid: z.string().uuid(),
});
export const ProcessorResourceDesignatorSchema: typeof ProcessorResourceDesignatorSchemaBase =
    ProcessorResourceDesignatorSchemaBase;

const MerchantAccountResourceDesignatorSchemaBase = z.object({
    resource_type: z.literal(ResourceType.MERCHANT_ACCOUNT),
    merchant_account_uuid: z.string().uuid(),
});
export const MerchantAccountResourceDesignatorSchema: typeof MerchantAccountResourceDesignatorSchemaBase =
    MerchantAccountResourceDesignatorSchemaBase;

const LogicalTerminalResourceDesignatorSchemaBase = z.object({
    resource_type: z.literal(ResourceType.LOGICAL_TERMINAL),
    logical_terminal_uuid: z.string().uuid(),
});
export const LogicalTerminalResourceDesignatorSchema: typeof LogicalTerminalResourceDesignatorSchemaBase =
    LogicalTerminalResourceDesignatorSchemaBase;

const TerminalDeviceResourceDesignatorSchemaBase = z.object({
    resource_type: z.literal(ResourceType.TERMINAL_DEVICE),
    terminal_device_uuid: z.string().uuid(),
});
export const TerminalDeviceResourceDesignatorSchema: typeof TerminalDeviceResourceDesignatorSchemaBase =
    TerminalDeviceResourceDesignatorSchemaBase;

const PermissionsBundleResourceDesignatorSchemaBase = z.object({
    resource_type: z.literal(ResourceType.PERMISSIONS_BUNDLE),
    permissions_bundle_uuid: z.string().uuid(),
});
export const PermissionsBundleResourceDesignatorSchema: typeof PermissionsBundleResourceDesignatorSchemaBase =
    PermissionsBundleResourceDesignatorSchemaBase;

const PermissionPolicyResourceDesignatorSchemaBase = z.object({
    resource_type: z.literal(ResourceType.PERMISSION_POLICY),
    permission_policy_uuid: z.string().uuid(),
});
export const PermissionPolicyResourceDesignatorSchema: typeof PermissionPolicyResourceDesignatorSchemaBase =
    PermissionPolicyResourceDesignatorSchemaBase;

const RpaModuleResourceDesignatorSchemaBase = z.object({
    resource_type: z.literal(ResourceType.RPA_MODULE),
    rpa_module_name: RpaModuleNameSchema,
});
export const RpaModuleResourceDesignatorSchema: typeof RpaModuleResourceDesignatorSchemaBase =
    RpaModuleResourceDesignatorSchemaBase;

const WildcardPermissionSourceDesignatorSchemaBase = z.object({
    designator_type: z.literal(PermissionSourceDesignatorType.WILDCARD),
});
export const WildcardPermissionSourceDesignatorSchema: typeof WildcardPermissionSourceDesignatorSchemaBase =
    WildcardPermissionSourceDesignatorSchemaBase;

const ResourceDesignatorSchemaBase = z.discriminatedUnion('resource_type', [
    OrganizationResourceDesignatorSchemaBase,
    UserResourceDesignatorSchemaBase,
    RoleAssignmentResourceDesignatorSchemaBase,
    CredentialResourceDesignatorSchemaBase,
    IpwResourceDesignatorSchemaBase,
    IpgResourceDesignatorSchemaBase,
    ProcessorResourceDesignatorSchemaBase,
    MerchantAccountResourceDesignatorSchemaBase,
    LogicalTerminalResourceDesignatorSchemaBase,
    TerminalDeviceResourceDesignatorSchemaBase,
    PermissionsBundleResourceDesignatorSchemaBase,
    PermissionPolicyResourceDesignatorSchemaBase,
    RpaModuleResourceDesignatorSchemaBase,
]);
export const ResourceDesignatorSchema: typeof ResourceDesignatorSchemaBase =
    ResourceDesignatorSchemaBase;

const PermissionSourceDesignatorSchemaBase = z.union([
    ResourceDesignatorSchema,
    WildcardPermissionSourceDesignatorSchema,
]);
export const PermissionSourceDesignatorSchema: z.ZodType<PermissionSourceDesignator> =
    PermissionSourceDesignatorSchemaBase;

/**
 * Permission grant/permission object
 */
export interface PermissionsGrant {
    resource_type: ResourceType;
    resources: PermissionSourceDesignator[];
    actions: AuthorizableAction[];
}

/**
 * Permission bundle
 */
export interface PermissionsBundle {
    permissions_bundle_id: number;
    name: string;
    description: string | null;
    permissions: PermissionsGrant[];
    is_personal: boolean;
    is_active: boolean;
}

export interface PermissionsBundlePublic {
    permissions_bundle_uuid: string;
    name: string;
    description: string | null;
    permissions: PermissionsGrant[];
    is_personal: boolean;
    is_active: boolean;
    permissions_deserialization_error?: string;
}

export interface PermissionSourceRoleAssignmentListItem {
    role_assignment_uuid: string;
    user_uuid: string;
    user_display_name: string;
    primary_email: string;
    role: Role;
    organization_uuid: string;
    organization_name: string;
}

export interface OrganizationScopedPermissionSourceOptionsResponse {
    enabled_ipw_system_ids: IpwSystemId[];
    enabled_ipg_system_ids: IpgSystemId[];
}

/**
 * Role assignment for authorization checks
 * This is the canonical type used throughout the backend
 * Represents the active role assignment with its permission bundle
 */
export interface RoleAssignment {
    role_assignment_id: number;
    role_assignment_uuid?: string;
    user_id: number;
    organization_id: number | null;
    organization_uuid?: string;
    role: Role;
    organization_name: string | null; // Optional: organization name for display (frontend use)
    organization_is_iso: boolean;
    organization_is_merchant: boolean;
    organization_is_customer: boolean;
}

export interface RoleAssignmentPermissionContext {
    role: Role;
    organization_name: string | null;
    organization_is_iso: boolean;
    organization_is_merchant: boolean;
    organization_is_customer: boolean;
}

export interface RoleAssignmentDetails {
    permissions_bundle: PermissionsBundle;
    combined_config: CombinedConfigResponse;
}

/**
 * Role assignment with permissions
 */
export type RoleAssignmentDetailed = RoleAssignment & RoleAssignmentDetails;

export interface RoleAssignmentPermissionsView extends RoleAssignmentPermissionContext {
    role_assignment_uuid: string;
    organization_uuid: string | null;
    permissions_bundle: PermissionsBundlePublic;
    combined_config: CombinedConfigResponse;
}

/**
 * Grant permission request
 */
export interface GrantPermissionRequest {
    role_assignment_uuid: string;
    permission: PermissionsGrant;
}

/**
 * Revoke permission request
 */
export interface RevokePermissionRequest {
    role_assignment_uuid: string;
    permission: PermissionsGrant;
}

/**
 * Assign bundle request
 */
export interface AssignBundleRequest {
    role_assignment_uuid: string;
    bundle_uuid: string;
}

/**
 * Create shared bundle request
 */
export interface CreateSharedBundleRequest {
    name: string;
    description?: string;
    permissions: PermissionsGrant[];
}

/**
 * Update bundle request
 */
export interface UpdateBundleRequest {
    name?: string;
    description?: string;
    permissions?: PermissionsGrant[];
    is_active?: boolean;
}

/**
 * List bundles response
 */
export interface ListBundlesResponse {
    bundles: PermissionsBundle[];
}

export interface ListPublicBundlesResponse {
    bundles: PermissionsBundlePublic[];
}

export interface SharedPermissionBundlesSearchRequest
    extends OpaqueCursorSearchRequest {}

export interface SharedPermissionBundlesSearchPageResponse {
    bundles: PermissionsBundlePublic[];
    pagination: OpaqueCursorPagination;
}

/**
 * Policy bundle search (org-scoped, cursor-paginated).
 */
export interface PolicyPermissionBundlesSearchRequest extends OpaqueCursorSearchRequest {}

export interface PolicyPermissionBundlesSearchPageResponse {
    bundles: PermissionsBundlePublic[];
    pagination: OpaqueCursorPagination;
}

export interface PermissionSourceRoleAssignmentsSearchRequest
    extends OpaqueCursorSearchRequest {}

export interface PermissionSourceRoleAssignmentsSearchPageResponse {
    role_assignments: PermissionSourceRoleAssignmentListItem[];
    pagination: OpaqueCursorPagination;
}

export interface PolicyPermissionBundleRoleAssignmentListItem
    extends PermissionSourceRoleAssignmentListItem {
    is_granted: boolean;
}

export interface PolicyPermissionBundleRoleAssignmentsSearchRequest
    extends OpaqueCursorSearchRequest {}

export interface PolicyPermissionBundleRoleAssignmentsSearchPageResponse {
    role_assignments: PolicyPermissionBundleRoleAssignmentListItem[];
    pagination: OpaqueCursorPagination;
}

/**
 * Create/update policy bundles.
 *
 * Note: We intentionally reuse the same shape as shared bundle CRUD; the difference is the
 * owning organization scope (path param) and invariants enforced in backend.
 */
export interface CreatePolicyPermissionBundleRequest extends CreateSharedBundleRequest {}
export interface UpdatePolicyPermissionBundleRequest extends UpdateBundleRequest {}

export interface RepairAuditedPermissionRequest {
    permissions_bundle_uuid: string;
    permission_index: number;
    permission: PermissionsGrant;
}

/**
 * Policy grants: assign one or more policy bundles to a role assignment (many-to-many).
 */
export interface ListPolicyPermissionGrantsResponse {
    bundles: PermissionsBundlePublic[];
}

export interface GrantPolicyPermissionBundleRequest {
    permissions_bundle_uuid: string;
}

export const PermissionGrantAuditFindingCode = {
    INVALID_PERMISSION_PAYLOAD: 'INVALID_PERMISSION_PAYLOAD',
    INVALID_POLICY_GRANT: 'INVALID_POLICY_GRANT',
    SOURCE_TARGET_PAIR_UNSUPPORTED: 'SOURCE_TARGET_PAIR_UNSUPPORTED',
    SOURCE_RESOURCE_NOT_FOUND: 'SOURCE_RESOURCE_NOT_FOUND',
    SOURCE_OUTSIDE_OBSERVABLE_UNIVERSE: 'SOURCE_OUTSIDE_OBSERVABLE_UNIVERSE',
} as const;

export type PermissionGrantAuditFindingCode =
    (typeof PermissionGrantAuditFindingCode)[keyof typeof PermissionGrantAuditFindingCode];

export interface PermissionGrantAuditFinding {
    permissions_bundle_uuid: string | null;
    permissions_bundle_name: string | null;
    permissions_bundle_is_personal: boolean | null;
    permission_index: number | null;
    resource_index: number | null;
    code: PermissionGrantAuditFindingCode;
    message: string;
    source_designator: PermissionSourceDesignator | null;
    raw_value_json: string | null;
    editable_permission: PermissionsGrant | null;
}

export interface RoleAssignmentPermissionAudit {
    role_assignment_uuid: string;
    user_uuid: string;
    user_display_name: string;
    primary_email: string;
    organization_uuid: string | null;
    organization_name: string | null;
    findings: PermissionGrantAuditFinding[];
}

export interface RoleAssignmentPermissionAuditResponse {
    audit: RoleAssignmentPermissionAudit;
}

export interface OrganizationPermissionAuditResponse {
    organization_uuid: string;
    organization_name: string;
    audits: RoleAssignmentPermissionAudit[];
}

/**
 * Role eligibility for actions
 */
export interface RoleEligibility {
    role: string;
    eligible_actions: AuthorizableAction[];
}

export const RoleSchema = z.enum(Role);
export const InternalRoleSchema = z.enum(AllInternalOnly);
export const OrganizationalRoleSchema = z.enum(AllOrganizationalOnly);
const PermissionGrantAuditFindingCodeSchemaBase = z.enum([
    PermissionGrantAuditFindingCode.INVALID_PERMISSION_PAYLOAD,
    PermissionGrantAuditFindingCode.INVALID_POLICY_GRANT,
    PermissionGrantAuditFindingCode.SOURCE_TARGET_PAIR_UNSUPPORTED,
    PermissionGrantAuditFindingCode.SOURCE_RESOURCE_NOT_FOUND,
    PermissionGrantAuditFindingCode.SOURCE_OUTSIDE_OBSERVABLE_UNIVERSE,
]);
export const PermissionGrantAuditFindingCodeSchema: z.ZodType<PermissionGrantAuditFindingCode> =
    PermissionGrantAuditFindingCodeSchemaBase;
const PermissionGrantSchemaBase = z
    .object({
        resource_type: z.enum(ResourceType),
        resources: z.array(PermissionSourceDesignatorSchema).min(1),
        actions: z.array(z.enum(ALL_AUTHORIZABLE_ACTIONS)).min(1),
    })
    .superRefine((value, context) => {
        for (const action of value.actions) {
            if (!isAllowedActionForResourceType(value.resource_type, action)) {
                context.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Action ${action} is not allowed for resource_type ${value.resource_type}`,
                    path: ['actions'],
                });
            }
        }
    });

export const PermissionGrantSchema: typeof PermissionGrantSchemaBase =
    PermissionGrantSchemaBase;

export const PermissionGrantArraySchema: z.ZodType<PermissionsGrant[]> = z.array(
    PermissionGrantSchema
);
export const PermissionsBundleSchema = z.object({
    permissions_bundle_id: z.number(),
    name: z.string(),
    description: z.string().nullable(), // Handles 'string | null'
    permissions: z.array(PermissionGrantSchema), // Validates the array of grants
    is_personal: z.boolean(),
    is_active: z.boolean(),
});
export const PermissionsBundlePublicSchema: z.ZodType<PermissionsBundlePublic> = z.object({
    permissions_bundle_uuid: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    permissions: z.array(PermissionGrantSchema),
    is_personal: z.boolean(),
    is_active: z.boolean(),
    permissions_deserialization_error: z.string().optional(),
});
const PermissionSourceRoleAssignmentListItemSchemaBase = z.object({
    role_assignment_uuid: z.string().uuid(),
    user_uuid: z.string().uuid(),
    user_display_name: z.string().min(1),
    primary_email: z.string().email(),
    role: z.enum(Role),
    organization_uuid: z.string().uuid(),
    organization_name: z.string().min(1),
});
export const PermissionSourceRoleAssignmentListItemSchema: z.ZodType<PermissionSourceRoleAssignmentListItem> =
    PermissionSourceRoleAssignmentListItemSchemaBase;

const PolicyPermissionBundleRoleAssignmentListItemSchemaBase =
    PermissionSourceRoleAssignmentListItemSchemaBase.extend({
        is_granted: z.boolean(),
    });
export const PolicyPermissionBundleRoleAssignmentListItemSchema: z.ZodType<PolicyPermissionBundleRoleAssignmentListItem> =
    PolicyPermissionBundleRoleAssignmentListItemSchemaBase;

const OrganizationScopedPermissionSourceOptionsResponseSchemaBase = z.object({
    enabled_ipw_system_ids: z.array(IpwResourceDesignatorSchema.shape.ipw_system_id),
    enabled_ipg_system_ids: z.array(IpgResourceDesignatorSchema.shape.ipg_system_id),
});
export const OrganizationScopedPermissionSourceOptionsResponseSchema: z.ZodType<OrganizationScopedPermissionSourceOptionsResponse> =
    OrganizationScopedPermissionSourceOptionsResponseSchemaBase;
export const RoleAssignmentSchema: z.ZodType<RoleAssignment> = z.object({
    role_assignment_id: z.number(),
    role_assignment_uuid: z.string().uuid().optional(),
    user_id: z.number(),
    organization_id: z.number().nullable(),
    organization_uuid: z.string().uuid().optional(),
    role: z.enum(Role),
    organization_name: z.string().nullable(),
    organization_is_iso: z.boolean(),
    organization_is_merchant: z.boolean(),
    organization_is_customer: z.boolean(),
});

export const RoleAssignmentDetailedSchema: z.ZodType<RoleAssignmentDetailed> = z.object({
    role_assignment_id: z.number(),
    role_assignment_uuid: z.string().uuid().optional(),
    user_id: z.number(),
    organization_id: z.number().nullable(),
    organization_uuid: z.string().uuid().optional(),
    role: z.enum(Role),
    organization_name: z.string().nullable(),
    organization_is_iso: z.boolean(),
    organization_is_merchant: z.boolean(),
    organization_is_customer: z.boolean(),
    permissions_bundle: PermissionsBundleSchema,
    combined_config: CombinedConfigResponseSchema,
});

export const RoleAssignmentPermissionsViewSchema: z.ZodType<RoleAssignmentPermissionsView> =
    z.object({
        role_assignment_uuid: z.string().uuid(),
        organization_uuid: z.string().uuid().nullable(),
        role: z.enum(Role),
        organization_name: z.string().nullable(),
        organization_is_iso: z.boolean(),
        organization_is_merchant: z.boolean(),
        organization_is_customer: z.boolean(),
        permissions_bundle: PermissionsBundlePublicSchema,
        combined_config: CombinedConfigResponseSchema,
    });

export const PermissionGrantAuditFindingSchema: z.ZodType<PermissionGrantAuditFinding> = z.object({
    permissions_bundle_uuid: z.string().uuid().nullable(),
    permissions_bundle_name: z.string().nullable(),
    permissions_bundle_is_personal: z.boolean().nullable(),
    permission_index: z.number().int().nullable(),
    resource_index: z.number().int().nullable(),
    code: PermissionGrantAuditFindingCodeSchema,
    message: z.string(),
    source_designator: PermissionSourceDesignatorSchema.nullable(),
    raw_value_json: z.string().nullable(),
    editable_permission: PermissionGrantSchema.nullable(),
});

export const RoleAssignmentPermissionAuditSchema: z.ZodType<RoleAssignmentPermissionAudit> =
    z.object({
        role_assignment_uuid: z.string().uuid(),
        user_uuid: z.string().uuid(),
        user_display_name: z.string(),
        primary_email: z.string().email(),
        organization_uuid: z.string().uuid().nullable(),
        organization_name: z.string().nullable(),
        findings: z.array(PermissionGrantAuditFindingSchema),
    });

export const RoleAssignmentPermissionAuditResponseSchema: z.ZodType<RoleAssignmentPermissionAuditResponse> =
    z.object({
        audit: RoleAssignmentPermissionAuditSchema,
    });

export const OrganizationPermissionAuditResponseSchema: z.ZodType<OrganizationPermissionAuditResponse> =
    z.object({
        organization_uuid: z.string().uuid(),
        organization_name: z.string(),
        audits: z.array(RoleAssignmentPermissionAuditSchema),
    });

export const AllRoleAssignmentsWithPermissionsSchema = z.array(RoleAssignmentDetailedSchema);
